import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveAvatarUrl } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { ShieldCheck, AlertTriangle, Briefcase } from "lucide-react";

/**
 * Who posted this job, and have they ever actually paid anyone.
 *
 * Upwork leads its job page with a client panel for a reason: an agent about to
 * spend a bid is extending credit to a stranger. This page showed nothing at
 * all about the poster.
 *
 * The figures are the honest ones. `jobsFunded` counts escrow actually
 * deposited and `totalSettledUsdc` counts money that reached an agent, so a
 * poster who has posted ten jobs and funded none reads as exactly that. That is
 * the point — flattering a poster here costs an agent real work.
 */

export interface PosterStats {
  id: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  memberSince?: string | null;
  jobsPosted: number;
  jobsFunded: number;
  jobsCompleted: number;
  totalSettledUsdc: number;
  fundingRate: number;
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`text-sm font-semibold ${tone ?? ""}`}>{value}</div>
    </div>
  );
}

export function PosterCard({
  stats,
  isPoster,
}: {
  stats?: PosterStats | null;
  isPoster: boolean;
}) {
  if (!stats) return null;

  const name = stats.displayName || "Job poster";
  const pct = Math.round((stats.fundingRate ?? 0) * 100);

  // A poster with a track record of funding is the signal worth showing; one
  // with postings and no funding is the signal worth warning about.
  const proven = stats.jobsCompleted > 0;
  const neverFunded = stats.jobsPosted > 1 && stats.jobsFunded === 0;

  return (
    <Card className="glass-panel">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-11 w-11">
            <AvatarImage src={resolveAvatarUrl(stats.avatarUrl ?? undefined)} />
            <AvatarFallback>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold leading-tight truncate">
              {name}
              {isPoster && (
                <span className="ml-2 text-xs text-muted-foreground">(you)</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {stats.memberSince
                ? `Member for ${formatDistanceToNow(new Date(stats.memberSince))}`
                : "Member"}
            </div>
          </div>
        </div>

        {proven ? (
          <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/40">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Has paid {stats.totalSettledUsdc} USDC to agents
          </Badge>
        ) : neverFunded ? (
          <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/40">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Has never funded a job
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            <Briefcase className="h-3 w-3 mr-1" />
            No completed jobs yet
          </Badge>
        )}

        <Separator />

        <div className="grid grid-cols-2 gap-3">
          <Stat label="Jobs posted" value={String(stats.jobsPosted)} />
          <Stat
            label="Funded"
            value={`${stats.jobsFunded}${stats.jobsPosted ? ` · ${pct}%` : ""}`}
            tone={
              stats.jobsPosted > 0 && pct < 50 ? "text-amber-400" : undefined
            }
          />
          <Stat label="Completed" value={String(stats.jobsCompleted)} />
          <Stat
            label="Paid out"
            value={`${stats.totalSettledUsdc} USDC`}
            tone={stats.totalSettledUsdc > 0 ? "text-emerald-400" : undefined}
          />
        </div>

        {!isPoster && neverFunded && (
          <p className="text-xs text-muted-foreground">
            This poster has published work before without depositing escrow.
            Check that this job is funded before spending a bid on it.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
